import type {
  PassengersForFareRequest,
  Person,
} from "@b2bportal/air-booking-api";

import { getChildState, getNestedChildState } from "@checkout/helpers";
import {
  ParentState,
  PassengerErrorModalTypes,
  PassengerInformationChildState,
  PassengerInformationState,
} from "@checkout/types";
import { getDateTimeWithFormat } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import type { State } from "xstate";
import type { FlightContextV2 } from "../../types";

type PassengerStateType = State<FlightContextV2>;
type PassengerStateWithoutValue = Pick<PassengerStateType, "context">;

export const getAddPassport = ({ context }: PassengerStateWithoutValue) =>
  context[ParentState.passengerInformation].addPassport;

export const getSelectedTripInPassenger = ({
  context,
}: PassengerStateWithoutValue) => context.flightShop.selectedTrip;

export const getIsPassengerInformationLoading = (state: PassengerStateType) => {
  const childState = getChildState(state.value) as PassengerInformationState;
  const nestedChildState = getNestedChildState(
    state.value
  ) as PassengerInformationChildState;

  return (
    childState === PassengerInformationState.loading ||
    [
      PassengerInformationChildState.add,
      PassengerInformationChildState.delete,
      PassengerInformationChildState.update,
    ].includes(nestedChildState)
  );
};

export const getUserPassengers = ({ context }: PassengerStateWithoutValue) =>
  context[ParentState.passengerInformation].userPassengers;

export const getCurrentInfantId = ({ context }: PassengerStateWithoutValue) =>
  context[ParentState.passengerInformation].currentInfantId;

export const getSelectedPassengerIds = ({
  context,
}: PassengerStateWithoutValue) =>
  context[ParentState.passengerInformation].selectedPassengerIds;

export const getSelectedLapInfantIds = ({
  context,
}: PassengerStateWithoutValue) =>
  context[ParentState.passengerInformation].selectedLapInfantIds;

export const getAllSelectedPassengerIds = (
  state: PassengerStateWithoutValue
) => {
  const selectedPassengerIds = getSelectedPassengerIds(state);
  const selectedLapInfantIds = getSelectedLapInfantIds(state);
  return [...selectedPassengerIds, ...selectedLapInfantIds];
};

export const getAllSelectedUserPassengers = (
  state: PassengerStateWithoutValue
) => {
  const selectedPassengerIds = getSelectedPassengerIds(state);
  const selectedLapInfantIds = getSelectedLapInfantIds(state);
  const userPassengers = getUserPassengers(state);

  return userPassengers.filter((passenger) =>
    [...selectedPassengerIds, ...selectedLapInfantIds].includes(passenger.id)
  );
};

export const getPassengerStateValidated = (state: PassengerStateWithoutValue) =>
  getSelectedPassengerIds(state).length > 0;

export const getSearchPassengerNumber = ({
  context,
}: PassengerStateWithoutValue) => {
  let passengerCount = 0;
  context.flightShop.tripDetails.fareDetails[0].paxPricings.forEach(
    (pricing) => (passengerCount += pricing.quantity)
  );
  return passengerCount;
};

export const getNumPassengerAlertDismissed = ({
  context,
}: PassengerStateWithoutValue) =>
  context[ParentState.passengerInformation].numPassengerAlertDismissed;

export const getCurrentUserPassenger = ({
  context,
}: PassengerStateWithoutValue) =>
  context[ParentState.passengerInformation].currentUserPassenger;

export const getOpenPassengerFormModal = (state: PassengerStateType) =>
  PassengerInformationState.passengerForm ===
  (getChildState(state.value) as PassengerInformationState);

export const getOpenPassengerPicker = (state: PassengerStateType) =>
  (getChildState(state.value) as PassengerInformationState) ===
  PassengerInformationState.passengerPicker;

export const getIsLapInfant = (traveler: Person, departureDate?: Date) => {
  const age = departureDate
    ? dayjs(new Date(departureDate)).diff(dayjs(traveler.dateOfBirth), "year")
    : dayjs().diff(
        getDateTimeWithFormat(traveler.dateOfBirth, "YYYY-MM-DD"),
        "year"
      );
  return age < 2;
};

export const getPassengerError = ({ context }: PassengerStateWithoutValue) =>
  context[ParentState.passengerInformation].error;

export const getPassengerErrorOpen = (state: PassengerStateType) => {
  // Return main errors
  const passengerError = getPassengerError(state);

  if (passengerError) {
    return passengerError;
  }

  // If the flight excludes seated infants and there is one, show error
  const userPassengers = getUserPassengers(state);
  const selectedPassngers = getSelectedPassengerIds(state);
  const fareExcludesINS = getFareExcludesINS(state);
  const departureDate = state.context.flightSearch.departureDate;
  const hasSeatedInfant = selectedPassngers.some((pId) => {
    const person = userPassengers.find((p) => p.id === pId);
    if (!person) return false;

    return getIsLapInfant(person, departureDate);
  });

  if (hasSeatedInfant && fareExcludesINS) {
    return {
      type: PassengerErrorModalTypes.INSNotAllowed,
      data: "",
    };
  }

  const infantOnlyError = getInfantOnlyError(state);
  if (infantOnlyError) {
    return {
      type: PassengerErrorModalTypes.UnaccompaniedMinor,
      data: "",
    };
  }

  return undefined;
};

export const getInfantOnlyError = ({ context }: PassengerStateWithoutValue) =>
  context[ParentState.passengerInformation].infantOnlyError;

export const getSelectedTrip = ({ context }: PassengerStateWithoutValue) =>
  context.flightShop?.selectedTrip;

export const getSelectedFareId = (state: PassengerStateWithoutValue) => {
  const selectedTrip = getSelectedTrip(state);
  return selectedTrip
    ? selectedTrip.returnFareId
      ? selectedTrip.returnFareId
      : selectedTrip.outgoingFareId
    : undefined;
};

export const getTripDetails = ({ context }: PassengerStateWithoutValue) =>
  context.flightShop?.tripDetails;

export const getFareExcludesINS = (state: PassengerStateWithoutValue) => {
  const selectedFareId = getSelectedFareId(state);
  const selectedTripDetails = getTripDetails(state);

  return (
    selectedFareId &&
    selectedTripDetails?.fareDetails
      ?.filter((fare) => fare.id === selectedFareId)
      ?.some((fare) => !!fare.excludeINS)
  );
};

export const getTripPriceParams = (
  state: PassengerStateWithoutValue
): PassengersForFareRequest => {
  const selectedTrip = getSelectedTrip(state);
  const selectedPassengerIds = getSelectedPassengerIds(state);
  const selectedLapInfants = getSelectedLapInfantIds(state);

  const fareId = selectedTrip.returnFareId
    ? selectedTrip.returnFareId
    : selectedTrip.outgoingFareId || "";
  const defaultPricingParams = {
    tripId: selectedTrip.tripId || "",
    seatedPassengers: selectedPassengerIds,
    lapInfants: selectedLapInfants,
    fareId: fareId,
    // TODO: Add ancilliary ids here
    ancillaryIds: [],
  };

  return defaultPricingParams;
};

export const getPassengerVisited = (state: PassengerStateWithoutValue) =>
  state.context[ParentState.passengerInformation].visited;

export const getIsInfantSeatModalLoading = (
  state: PassengerStateWithoutValue
) => state.context.common.tripPricing === undefined;

export const getUserPassengersParent = (state: PassengerStateWithoutValue) =>
  state.context[ParentState.passengerInformation].userPassengers;

export const getSelectedPassengerIdsParent = (
  state: PassengerStateWithoutValue
) => state.context[ParentState.passengerInformation].selectedPassengerIds;

export const getSelectedLapInfantIdsParent = (
  state: PassengerStateWithoutValue
) => state.context[ParentState.passengerInformation].selectedLapInfantIds;

export const getAllSelectedPassengerIdsParent = (
  state: PassengerStateWithoutValue
) => {
  const selectedPassengerIds = getSelectedPassengerIdsParent(state);
  const selectedLapInfantIds = getSelectedLapInfantIdsParent(state);
  return [...selectedPassengerIds, ...selectedLapInfantIds];
};

export const getAllSelectedUserPassengersParent = (
  state: PassengerStateWithoutValue
) => {
  const selectedPassengerIds = getAllSelectedPassengerIdsParent(state);
  const userPassengers = getUserPassengersParent(state);

  return userPassengers.filter((passenger) =>
    selectedPassengerIds.includes(passenger.id)
  );
};

export const getSeatedPassengersParent = (
  state: PassengerStateWithoutValue
) => {
  const selectedPassengerIds = getSelectedPassengerIdsParent(state);
  const userPassengers = getUserPassengersParent(state);

  return userPassengers.filter((passenger) =>
    selectedPassengerIds.includes(passenger.id)
  );
};

export const getIsEditing = (state: PassengerStateWithoutValue) => {
  return state.context[ParentState.passengerInformation].isEditing;
};

export const getLastSelectedPassengerId = (state: PassengerStateWithoutValue) =>
  state.context[ParentState.passengerInformation].lastSelectedPassengerId || "";
